// Header "MANUALS" chip + full-screen overlay hosting the My Manuals section.
// Rendered only for logged-in users (and only when Supabase is configured);
// logged-out visitors see the app exactly as before.
//
// Like the auth modal, the overlay is portaled to document.body because the
// header's backdrop-filter would otherwise anchor position:fixed to itself.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../auth/AuthContext';
import MyManuals from './MyManuals';

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'var(--page-bg, #e9e7f7)',
  zIndex: 1500, // above the header (50), below the auth modal (2000)
  overflowY: 'auto',
  padding: '20px 12px calc(20px + env(safe-area-inset-bottom))',
};

// Exported so the home page (My Manuals card, prompt bar) can open the same
// overlay without duplicating it.
export function ManualsOverlay({ onClose }) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return createPortal(
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="My Manuals">
      <div
        style={{
          maxWidth: 760,
          margin: '0 auto',
          background: 'var(--surface-1, #ffffff)',
          borderRadius: 24,
          boxShadow: 'var(--shadow-shell, 0 30px 80px -30px rgba(26,31,54,0.35))',
          padding: '24px 18px 48px',
          minHeight: 'calc(100vh - 40px)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 18,
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>My Manuals</div>
            <div
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10,
                color: 'var(--text-3)',
                letterSpacing: 1.5,
                marginTop: 2,
              }}
            >
              BRING YOUR OWN MANUAL
            </div>
          </div>
          <button className="btn btn-ghost" onClick={onClose} style={{ flexShrink: 0 }}>
            ← Back
          </button>
        </div>
        <MyManuals />
      </div>
    </div>,
    document.body
  );
}

export default function ManualsButton() {
  const { user, loading, isConfigured } = useAuth();
  const [open, setOpen] = useState(false);

  if (!isConfigured || loading || !user) return null;

  return (
    <>
      <button
        className="header-chip manuals-chip"
        onClick={() => setOpen(true)}
        aria-label="My Manuals"
      >
        📚 MANUALS
      </button>
      {open && <ManualsOverlay onClose={() => setOpen(false)} />}
    </>
  );
}
